import type React from "react";
import {
  type ArtShortSerializer,
  type TSearchAllItem,
  type ISearchArtItem,
  type ISearchPersonItem,
  type ISearchPublisherItem,
  type PersonRoleEnum,
  type CurrencyEnum,
  SearchResponseItemTypesEnum,
} from "@litres-libs/types";

import type { ISpotlightClickData } from "./utils";
import type { SearchItemTypes } from "./constants";

export interface IStringMap {
  [key: string]: string;
}

export interface IResultsListProps {
  searchResults?: SearchingResults["result"];
  searchString?: string;
  resultsListRef:
    | React.RefObject<HTMLUListElement>
    | React.RefCallback<HTMLElement>;
  isUserAnonymous: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  saveSearchQuery: (q: string) => void;
  searchID: string;
  isDesktop?: boolean;
  onClose: () => void;
}

export interface ISearchHistoryProps {
  history: ILastSearch[];
  setHistoryRefreshed: React.Dispatch<React.SetStateAction<boolean>>;
  searchHistoryListRef:
    | React.RefObject<HTMLUListElement>
    | React.RefCallback<HTMLElement>;
  isUserAnonymous: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isDesktop?: boolean;
  onClose: () => void;
}

export type SearchItemType =
  | ISearchArt
  | ISearchAuthor
  | ISearchTag
  | ISearchCollection
  | ISearchSeries
  | ISearchPublishing;

export interface SearchingResults {
  search_string: string;
  limit: number;
  result?: TSearchAllItem[];
  page_data: {
    rmd_search: {
      stat: {
        has_request_result: number;
        has_searchers_purchases: number;
      };
    };
  };
  q_parsed: {
    search_str: string;
    Canonical: string;
    WordsArr: string[];
    limit: number;
  };
}

export interface SellableItemProps {
  externalRef: React.RefObject<HTMLLIElement>;
  /** ID */
  id: string | number;
  /** Url до товара на сайте */
  url: string | null;
  /** Название */
  title: string;
  /** Не используем */
  persons?: ArtShortSerializer["persons"];
  /** Текстовая или аудио */
  mem: string;
  /** Цена */
  price: number | null;
  /** Валюта */
  currency: CurrencyEnum | null;
  /** Цена со скидкой */
  promoPrice: number | null;
  /** Пользователь аноним */
  isUserAnonymous: boolean;
  /** Является ли книга бесплатной */
  free?: boolean;
  /** Является ли книга бесплатной 2 */
  freebie?: number;
  /** Сколько книг из имеющихся в библиотеке уже взято */
  busyInLibrary?: string;
  /** Сколько книг из имеющихся в библиотеке доступно */
  availableInLibrary?: string;
  /** Сразу можно брать или через запрос? */
  libraryServiceType?:
    | "delayed"
    | "instant"
    | "impossible"
    | "requested"
    | "received"
    | null;
  /** Книга доступна через слонораздачи и прочие промо */
  availableByPromo: ArtShortSerializer["is_available_with_subscription"];
  /** Добавленные */
  owned: number;
  /** Айдишник для скачивания/чтения */
  releaseFile: number;
  /** Еще какой-то айдишник */
  uuid: string;
  /** Процент прослушанного/прочитанного */
  percent: ArtShortSerializer["read_percent"];
  /** Отношение ширины к длине обложки */
  coverRatio: number | null;
  /** Какой-то тип */
  type: SearchResponseItemTypesEnum;
  /** Id файла */
  fileId: number;
  /** Формат обложки */
  cover: ArtShortSerializer["cover_url"];
  /** Формат файла*/
  fileType: string | null;
  /** Поисковая строка */
  searchQuery?: string;
  /** Установка загрузки */
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  /** Функция для сохранения запроса в БД */
  saveSearchQuery: (q: string) => void;
  /** Данные для LitresTracker */
  eventData: ISpotlightClickData;
  isDesktop?: boolean;
  item: ArtShortSerializer;
}

export interface UnsoldItemProps {
  externalRef: React.RefObject<HTMLLIElement>;
  /** Url на сайте */
  url: string;
  /** Тип книга, автор, жанр и т.д */
  itemType:
    | SearchResponseItemTypesEnum.tag
    | SearchResponseItemTypesEnum.collection
    | SearchResponseItemTypesEnum.series
    | SearchResponseItemTypesEnum.publisher
    | SearchResponseItemTypesEnum.person
    | SearchResponseItemTypesEnum.genre;
  role?: PersonRoleEnum;
  /** Название */
  title: string;
  /** ID */
  id: string | number;
  /** Не используем */
  arts?: ISmallSearchArt[];
  /** Пользователь аноним */
  isUserAnonymous: boolean;
  /** Поисковая строка */
  searchQuery?: string;
  /** Установка загрузки */
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  /** Формат логотипа издательства */
  smallLogo?: string;
  /** Функция для сохранения запроса в БД */
  saveSearchQuery: (q: string) => void;
  /** Данные для LitresTracker */
  eventData: ISpotlightClickData;
  isDesktop?: boolean;
}

export interface ISearchProps {
  searchResults?: SearchingResults;
  isUserAnonymous: boolean;
}

export interface ISearchArtPerson {
  total_arts: string;
  s_first_name: string;
  relation: string;
  s_middle_name: string;
  fake: string;
  s_last_name: string;
  s_relation_uilang: string;
  s_full_name: string;
  id: string;
  s_full_rodit: string;
  lvl: string;
}

export interface ISmallSearchArt {
  id: number;
  title: string;
  url: string;
  cover_url: string;
  cover_ratio: number;
}

export interface ISearchAuthor {
  id: string;
  smart_rating: number;
  title: string;
  role: string;
  item_type: "author";
  url: string;
  photo?: string;
}

export interface ISearchTag {
  arts: ISmallSearchArt[];
  smart_rating: number;
  url: string;
  item_type: "tag";
  arts_count: string;
  title: string;
  id: number;
}

export interface ISearchCollection {
  id: string;
  title: string;
  item_type: "collection";
  url: string;
  smart_rating: number;
  arts: ISmallSearchArt[];
}

export interface ISearchArt {
  biblio_busy: string;
  biblio_fund: string;
  biblio_my_request: number;
  biblio_queue_size: string;
  biblio_selfservice: "instant" | "delayed";
  book_avail_by_subscr: number;
  url: string;
  item_type: string;
  cover: string;
  pdf_page_size_y: string;
  cover_ratio: string;
  pdf_page_size_x: string;
  title: string;
  persons?: {
    person: ISearchArtPerson[];
  };
  price: string;
  s_mime: string;
  available: string;
  mem: string;
  file_type: string;
  file_id: number;
  promo_price: string;
  type: number;
  s_filename: string;
  release_file: number;
  smart_rating: number;
  size: string;
  chars: string;
  id: string;
  uuid: string;
  can_preorder: string;
  free: number;
  freebie: number;
  owned: number;
  percent: number;
}

export interface ISearchSeries {
  arts: ISmallSearchArt[];
  smart_rating: number;
  url: string;
  item_type: SearchItemTypes.Series;
  title: string;
  id: number;
}

export interface ISearchPublishing {
  arts: ISmallSearchArt[];
  smart_rating: number;
  url: string;
  item_type: SearchItemTypes.Publishing;
  title: string;
  id: number;
  small_logo?: string;
}

export interface ISearchHistoryItem {
  s_search: string;
  time: string;
}

export interface IDBSearchHistoryItem extends ISearchHistoryItem {
  id: string;
}

export const isSearchArt = (item: TSearchAllItem): item is ISearchArtItem => {
  return (
    item.type === SearchResponseItemTypesEnum.text_book ||
    item.type === SearchResponseItemTypesEnum.audiobook ||
    item.type === SearchResponseItemTypesEnum.podcast ||
    item.type === SearchResponseItemTypesEnum.podcast_episode
  );
};

export const isAuthor = (item: TSearchAllItem): item is ISearchPersonItem => {
  return (
    (item as ISearchPersonItem).type === SearchResponseItemTypesEnum.person
  );
};

export const isPublishing = (
  item: TSearchAllItem
): item is ISearchPublisherItem => {
  return (
    (item as ISearchPublisherItem).type ===
    SearchResponseItemTypesEnum.publisher
  );
};

export interface ILastSearch {
  value: string;
  source: "server" | "client";
}
